import { Button, Checkbox, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Radio, RadioGroup, Select, Stack, TextField } from '@mui/material';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import LabeledCheckbox from '../../../../TemplateProperties/TPCheckbox';
import CommonGrid from '../../../../Repository/CommonGrid';
import ResizableBox from '../../../../Common/ResizableBox';
import LightTooltip from '../../../../../commonComponents/LightTooltip';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { genKey, encryptString, decryptString } from '../../../../../utils/utilFunctions';
import { cloneDeep, debounce, isArray, isEqual, set } from 'lodash';
import WorkflowEncryptDecrypt from '../../Common/WorkflowEncryptDecrypt';
import { eachDeep } from 'deepdash-es/standalone';
import { treeActions } from '../../../../../redux/slices/treeSlice';
import { ParentScreenCtx } from '../../../../../pages/workbench_build/WorkbenchBuild';
import { MoreHorizOutlined } from '@mui/icons-material';
import WorkflowSessionModal from '../../../WorkflowSessionModal';
import SmartPasswordField from '../../../../../commonComponents/SmartPasswordField';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CustomUrlComposer from './CustomUrlComposer';

export default function BridgeGateHTTP({ connectionDetails, handleBodyContent, direction, screen }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const parentScreen = useContext(ParentScreenCtx);
  const rightTree = useSelector((state) => state.treeData.workflow.rightTree);
  const translatedData = rightTree.tree.body.translatedData;
  const selectedNode = useSelector((state) => state.treeData.workflow.rightTree.tree.body.selectedNode);
  const { account } = useSelector((state) => state.queryParams);

  const gridApiRef  = useRef();
  const [gridApi, setGridApi] = useState(null);
  const treeDataDefault = selectedNode?.INBOUND?.ARGS?.length > 0 ?
    selectedNode.INBOUND.ARGS.map((arg, index) => {
      return {
        ...arg,
        title: arg.ARGNAME,
        key: `${genKey('bghttpkeyrand')}-${index}`,
        id: index,
        children: [],
      }
    })
  : [];

  const [treeData, setTreeData] = useState(treeDataDefault);
  const treeDataRef = useRef(treeData);
  const [treeSelectedKeys, setTreeSelectedKeys] = useState(treeData?.length > 0 ? [treeData[0].key] : []);
  const [treeSelectedNode, setTreeSelectedNode] = useState(treeData?.length > 0 ? treeData[0] : []);

  const deleteTreeNodeByKey = (givenKey) => {
    const newTreeData = treeDataRef.current.filter((treeNode) => (treeNode.key !== givenKey && treeNode.id !== givenKey && String(treeNode.id) !== String(givenKey)));
    if (newTreeData) {
      setTreeData(newTreeData);
      treeDataRef.current = newTreeData;
      if (newTreeData.length) {
        setTreeSelectedKeys([newTreeData[0].key]);
        setTreeSelectedNode(newTreeData[0]);
        
      }
    }
  }

  // cell renderer for CommonGrid (replaces tree node display)
  const createCellRenderer = (params) => {
    const { data } = params;
    const handleDeleteClick = (e) => {
      e.stopPropagation();
      deleteTreeNodeByKey(data.key || data.id);
    }
    const iconSize = "18px";
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: "8px", width: "100%" }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1 }}>
          <span className="GET_DATA" style={{ height: iconSize, width: iconSize, display: 'inline-block' }} />
          <div className='truncated-text' title={data?.ARGNAME} style={{ flex: 1 }}>{data?.ARGNAME}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <LightTooltip title={t('DELETE')} arrow placement="right">
            <span style={{ cursor: 'pointer', height: iconSize, width: iconSize, backgroundSize: iconSize }} className="DELETE_MUI" onClick={handleDeleteClick}></span>
          </LightTooltip>
        </div>
      </div>
    )
  }


  const onAddClick = () => {
    const newTreeData = [...treeDataRef.current];
    const newKey = `${genKey('bghttpkeyrand')}-${newTreeData.length}`;
    //default properties for a new arg
    let newNodeData = {
      ARGNAME: "UNNAMED",
      ARGTYPE: "IN",
      title: "UNNAMED",
      key: newKey,
      id: newTreeData.length,
      children: [],
    }

    newTreeData.push(newNodeData);
    setTreeData(newTreeData);
    treeDataRef.current = newTreeData;
    setTreeSelectedKeys([newKey]);
    setTreeSelectedNode(newNodeData);
    // Use setTimeout to wait for grid to re-render with new data before selecting
    setTimeout(() => {
      gridApiRef?.current?.forEachNode((node) => {
        if (node?.data?.key === newKey) {
          node.setSelected(true);
          gridApiRef?.current.ensureNodeVisible(node);
        }
      });
    }, 100);
  }

  // gridOptions for CommonGrid (placed after onAddClick to ensure handler exists)
  const gridOptions = {
    columnDefs: [
      {
        addNewControl: true,
        addControlLabel: t('MNU_WS_NEW_IN_VALUE'),
        customClickHandlerOnAdd: onAddClick,
        headerName: "Edit In-Values:",
        field: 'ARGNAME',
        editable: false,
        resizable: false,
        cellRenderer: (info) => createCellRenderer(info),
        rowDrag: true,
      }
    ],
    onRowDragEnd: function(event) {
      const newTreeData = [];
      let newSelectedKey = null;
      const selectedRows = event.api.getSelectedRows();
      event.api.forEachNodeAfterFilterAndSort(function(node, i) {
        const row = { ...node.data, id: i };
        newTreeData.push(row);
        if (selectedRows?.[0] && selectedRows[0].key === node.data.key) newSelectedKey = node.data.key;
      });
      // update state; existing effect will sync to JSON
      setTreeData(newTreeData);
      treeDataRef.current = newTreeData;
      if (newSelectedKey) {
        setTreeSelectedKeys([newSelectedKey]);
        setTimeout(() => {
          gridApiRef?.current?.forEachNode((node) => {
            if(node?.data?.key === event?.node?.data?.key){
              node?.setSelected(true);
              gridApiRef?.current?.ensureNodeVisible(node);
            }
          })
        }, 100);
      }
    },
    rowDragManaged: true,
  };

  const [valueNameText, setValueNameText] = useState('');

  

  const saveTextChange = (text) => {
    const newTreeData = cloneDeep(treeData).map((node) => {
      let newNode = {...node};
      if (node.key === treeSelectedNode.key) {
        if (newNode.DESCRIPTION) node.DESCRIPTION = text;
        if (newNode.MAPNAME) node.MAPNAME = text;
        newNode.ARGNAME = text;
        newNode.title = text;
      }
      return newNode;
    });
    setTreeData(newTreeData);
    treeDataRef.current = newTreeData;
    setTimeout(() => {
      gridApi?.forEachNode((node) => {
        if (node?.data?.key === treeSelectedNode.key) {
          node.setSelected(true);
          gridApi.ensureNodeVisible(node);
        }
      });
    }, 100);
  }

  const debounceTextChange = useCallback(debounce(saveTextChange, 200), [treeData, treeSelectedNode]);

  const onValueNameTextChange = (e) => {
    if (typeof e?.target?.value !== 'string') return;
    setValueNameText(e.target.value);
    debounceTextChange(e.target.value);
  }

  useEffect(() => {
    setValueNameText(treeSelectedNode.title);
  }, [treeSelectedNode.key]);

  const authDropdownOptions = [
    <MenuItem key={'NO_AUTH'} value={'NO_AUTH'}>
      {t('NO_AUTH')}
    </MenuItem>,
    <MenuItem key={'TRUE'} value={'TRUE'}>
      {t('BASIC_AUTH')}
    </MenuItem>,
    <MenuItem key={'BEARER_TOKEN'} value={'BEARER_TOKEN'}>
      {t('BEARER_TOKEN')}
    </MenuItem>
  ]

  const handleAuthDropdownChange = (e) => {
    if (!e?.target?.value) return;
    saveDataToNode("AUTHENTICATE", e.target.value);
  }

  const saveDecryptionComponent = (data) => {
    if (!data) return;
    if (data.remove) {
      saveDataToNode(
        Object.entries(data.remove).map((entry) => {
          return {key: entry[0], value: entry[1]}
        }), 
        false, 
        true
      );
    } else if (data.add) {
      //bulk save properties to node
      saveDataToNode(
        Object.entries(data.add).map((entry) => {
          return {key: entry[0], value: entry[1]}
        }), 
        false, 
        true
      );
    } else {
      //save single property to node
      saveDataToNode(Object.keys(data)[0], Object.values(data)[0]);
    }
  }


  const handleSourceDataCbValue = (e) => {
    let newSelNode = {...treeSelectedNode};

    if (e?.target?.value === "sourceData") {
      //edit current node to WRITE_TO_RESPONSE: TRUE,
      // remove WRITE_TO_RESPONSE: TRUE from other nodes
      const newTreeData = cloneDeep(treeData).map((node, i) => {
        if (node.key === treeSelectedNode.key) {
          node.WRITE_TO_RESPONSE = "TRUE";
          node.DESCRIPTION = node.ARGNAME;
          delete node.MAPNAME;
          newSelNode = {...node};
        } else {
          node.MAPNAME = node.ARGNAME;
          delete node.DESCRIPTION;
          delete node.WRITE_TO_RESPONSE;
        }
        return node;
      });
      setTreeData(newTreeData);
      treeDataRef.current = newTreeData;
      
    } else if (e?.target?.value === "wfSession") {
      //remove WRITE_TO_RESPONSE: TRUE from current node
      //edit first node to WRITE_TO_RESPONSE: TRUE
      
      const newTreeData = cloneDeep(treeData).map((node, i) => {
        if (node.key === treeSelectedNode.key) {
          node.MAPNAME = node.ARGNAME;
          delete node.DESCRIPTION;
          delete node.WRITE_TO_RESPONSE;
          newSelNode = {...node};
        }
        return node;
      });
      setTreeData(newTreeData);
      treeDataRef.current = newTreeData;
    }

    setTreeSelectedNode(newSelNode);
    // Use setTimeout to wait for grid to re-render with new data before selecting
    setTimeout(() => {
      if (gridApi?.forEachNode) {
        gridApi.forEachNode((node) => {
          if (node?.data?.key === newSelNode.key) {
            node.setSelected(true);
            gridApi.ensureNodeVisible(node);
          }
        });
      }
    }, 0);
  }

  const saveDataToNode = (key, value, bulk, rootLevelData) => {
    if (!key) return;
    let newSelNode = {...selectedNode};

    // Clone and deeply iterate through the tree data to update the relevant node.
    const updatedData = eachDeep(cloneDeep(translatedData), (obj) => {
      if (obj?.key !== selectedNode?.key) return;

      // Handle root level data if provided
      if (rootLevelData) {
        Object.entries(rootLevelData).forEach(([rootKey, rootValue]) => {
          if (rootValue === null || rootValue === '') {
            delete obj[rootKey];
          } else {
            obj[rootKey] = rootValue;
          }
        });
      }

      if (obj.INBOUND) {
        if (bulk === true) {
          key.forEach((entry) => {
            if (!entry.value) delete obj.INBOUND[entry.key];
            else obj.INBOUND[entry.key] = entry.value;
          });
        } else {
          if (key === "ARGS") {
            if (value === null || (typeof value === 'object' && value?.length === 0)) {
              delete obj.INBOUND.ARGS;
            } else {
              obj.INBOUND.ARGS = value;
            }
          } else if (key === "REC_POST_DATA") {
            obj.INBOUND.REC_POST_DATA = value;
            if (value === "FALSE") delete obj.INBOUND.ZIP;
          } else if (key === "ZIP") {
            if (value === "TRUE") obj.INBOUND.ZIP = "TRUE";
            else {
              delete obj.INBOUND.ZIP;
              delete obj.INBOUND.DECOMPRESSION_DIRECTORY;
            }
          } else {
            if (value === null || value === '') delete obj.INBOUND[key];
            else obj.INBOUND[key] = value;
          }
        }
      }
      newSelNode = {...obj};
      
      if (isArray(obj?.children)) {
        return obj.children;
      }
    });

    //check for diff before prevent unnecessary dispatches
    if (isEqual(translatedData, updatedData)) return;

    // Dispatch an action to update the selected node in the state.
    dispatch(
      treeActions.setRightTreeSelectedNode({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: newSelNode,
      })
    );

    // Dispatch an action to update the translated tree data in the state.
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedData,
      })
    );
  }

  useEffect(() => {
    const filteredTreeData = treeData.map((node) => {
      let newNode = {
        ARGNAME: node.ARGNAME,
        ARGTYPE: node.ARGTYPE,
      };
      if (node.WRITE_TO_RESPONSE) newNode.WRITE_TO_RESPONSE = node.WRITE_TO_RESPONSE;
      if (node.DESCRIPTION) newNode.DESCRIPTION = node.DESCRIPTION;
      if (node.MAPNAME) newNode.MAPNAME = node.MAPNAME;
      return newNode;
    });

    if (
      filteredTreeData && 
      !isEqual(filteredTreeData, selectedNode.INBOUND.ARGS)
    ) {
      saveDataToNode("ARGS", filteredTreeData);
    }
  }, [treeData]);

  const getSafePathSegment = (value) => String(value || '').toLowerCase().replace(/\s+/g, '');

  const copyToClipboard = (text) => {
    if (!text) return;
    const fallbackCopy = () => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    };

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).catch(fallbackCopy);
    } else {
      fallbackCopy();
    }
  };

  const accountName = getSafePathSegment(account);
  const workflowGroupName = getSafePathSegment(rightTree?.path?.split('/')[0]);
  const workflowName = getSafePathSegment(rightTree?.path?.split('/')[1]);
  const hasBasicAuth = selectedNode?.INBOUND?.AUTHENTICATE === "TRUE";
  const inValueParams = (treeData || [])
    .map((arg) => (arg?.ARGNAME || '').trim())
    .filter(Boolean)
    .map((argName) => `${encodeURIComponent(argName)}=[${argName}]`);
  const queryParts = [
    ...(hasBasicAuth ? ["userName=[USER]", "password=[PWD]"] : []),
    ...inValueParams
  ];
  const queryString = queryParts.length ? `?${queryParts.join("&")}` : "";
  const baseUrlValue = `https://[SERVER_URL]/execute/${accountName}/${workflowGroupName}/${workflowName}${queryString}`;

  const customUrlBase = "https://[SERVER_URL]/execute/";
  const normalizeCustomUrlValue = (rawValue) => {
    const raw = (rawValue || '').trim();
    if (!raw) return '';
    if (/^https?:\/?\/?$/i.test(raw)) return raw;

    // If users paste a full URL, keep only the path segment after /execute/.
    if (raw.includes("://")) {
      try {
        const parsed = new URL(raw);
        const fullPath = `${parsed.pathname || ''}${parsed.search || ''}${parsed.hash || ''}`;
        const executePath = fullPath.split("/execute/")[1];
        if (executePath) return executePath.replace(/^\/+/, '');
        const normalizedPath = fullPath.replace(/^\/+/, '');
        if (normalizedPath) return normalizedPath;
        return raw;
      } catch (e) {
        // If parsing fails, fall through to simple normalization.
        return raw;
      }
    }

    return raw.replace(/^\/+execute\/+/i, '').replace(/^execute\/+/i, '');
  };
  const [customUrlValue, setCustomUrlValue] = useState(normalizeCustomUrlValue(selectedNode?.INBOUND?.CUSTOM_URL));
  const [showCustomUrl, setShowCustomUrl] = useState(
    selectedNode?.INBOUND?.USE_CUSTOM_URL === "TRUE" || !!selectedNode?.INBOUND?.CUSTOM_URL
  );
  const debounceCustomUrlChange = useCallback(
    debounce((value) => saveDataToNode("CUSTOM_URL", value || null), 200),
    [treeData, selectedNode]
  );
  const handleCustomUrlChange = (e) => {
    const newValue = normalizeCustomUrlValue(e?.target?.value);
    setCustomUrlValue(newValue);
    debounceCustomUrlChange(newValue);
  };
  const handleShowCustomUrlChange = (checked) => {
    setShowCustomUrl(checked);
    saveDataToNode("USE_CUSTOM_URL", checked ? "TRUE" : null);
    if (!checked) {
      setCustomUrlValue('');
      debounceCustomUrlChange.cancel?.();
      saveDataToNode("CUSTOM_URL", null);
    }
  };

  useEffect(() => {
    setCustomUrlValue(normalizeCustomUrlValue(selectedNode?.INBOUND?.CUSTOM_URL));
    setShowCustomUrl(
      selectedNode?.INBOUND?.USE_CUSTOM_URL === "TRUE" || !!selectedNode?.INBOUND?.CUSTOM_URL
    );
  }, [selectedNode?.key, selectedNode?.INBOUND?.CUSTOM_URL, selectedNode?.INBOUND?.USE_CUSTOM_URL]);
  const customUrlFullValue = customUrlValue ? `${customUrlBase}${customUrlValue}${queryString}` : "";
  

  const handleSyncResChange = (checked) => {
    const syncValue = checked ? "TRUE" : "FALSE";
    saveDataToNode("BTN_SEND_SYNC_RESPONSE", syncValue, false, { SYNCHRONOUS: syncValue });
  }

  const handleRecPostDataChange = (checked) => {
    saveDataToNode("REC_POST_DATA", checked ? "TRUE" : "FALSE");
    if (!checked || treeData.length === 0) return;
    //if true, set all tree nodes to workflow session
    const newTreeData = cloneDeep(treeData).map((node, i) => {
      node.MAPNAME = node.ARGNAME;
      delete node.DESCRIPTION;
      delete node.WRITE_TO_RESPONSE;
      
      return node;
    });

    //same for selected node
    const newSelNode = {...treeSelectedNode};
    newSelNode.MAPNAME = treeSelectedNode.ARGNAME;
    delete newSelNode.DESCRIPTION;
    delete newSelNode.WRITE_TO_RESPONSE;

    setTreeSelectedNode(newSelNode);
    setTimeout(() => {
      gridApi.forEachNode((node) => {
        if (node?.data?.id === treeSelectedNode.id) {
          node.setSelected(true);
          gridApi.ensureNodeVisible(node);
        }
      });
    }, 0);
    setTreeData(newTreeData);
    treeDataRef.current = newTreeData;
  }

  const handleDecompressionChange = (checked) => {
    saveDataToNode("ZIP", checked ? "TRUE" : null);
  }


  //"Specify Decompression Directory (optional)" - state only - checkbox
  const defaultSpecDecDir = selectedNode?.INBOUND?.DECOMPRESSION_DIRECTORY ? true : false;
  const [specDecDir, setSpecDecDir] = useState(defaultSpecDecDir);
  const handleSpecDecDirChange = (checked) => {
    setSpecDecDir(checked);
  }

  useEffect(() => {
    if (selectedNode?.INBOUND?.ZIP !== "TRUE") {
      setSpecDecDir(false);
    }
  }, [selectedNode?.INBOUND?.ZIP])

  //"Decompression Directory" - DECOMPRESSION_DIRECTORY - textbox
  const defaultDecDirTextVal = selectedNode?.INBOUND?.DECOMPRESSION_DIRECTORY || '';
  const [decDirTextVal, setDecDirTextVal] = useState(defaultDecDirTextVal);
  // const debounceDecDirTextChange = useCallback(debounce(saveDataToNode, 200), [saveDataToNode]);
  const debounceDecDirTextChange = useCallback(debounce((key, val) => saveDataToNode(key, val), 100), [treeData, selectedNode]);
  const handleDecDirTextChange = (val) => {
    if (typeof val !== 'string') return;
    setDecDirTextVal(val);
    debounceDecDirTextChange("DECOMPRESSION_DIRECTORY", (!val || val === '') ? null : val);
  }

  //"Password" - USE_ZIP_PASSWORD - checkbox
  const handleUseZipPasswordChange = (checked) => {
    saveDataToNode("USE_ZIP_PASSWORD", checked ? "TRUE" : "FALSE");
  }

  //"Password" - ZIP_PASSWORD - text field
  const defaultPasswordText = selectedNode?.INBOUND?.ZIP_PASSWORD ? decryptString(selectedNode.INBOUND.ZIP_PASSWORD, process.env.REACT_APP_SECRET_KEY2) : '';
  const [passwordText, setPasswordText] = useState(defaultPasswordText);
  const debouncePasswordTextChange = useCallback(debounce((key, val) => saveDataToNode(key, val), 100), [treeData, selectedNode]);
  const handlePasswordTextChange = (val) => {
    if (typeof val !== 'string') return;
    setPasswordText(val);
    const encryptedValue = val ? encryptString(val, process.env.REACT_APP_SECRET_KEY2) : null;
    debouncePasswordTextChange("ZIP_PASSWORD", encryptedValue);
  }

  // wf session modal popup for Decompression Directory
  const [workflowSessionModalDD, setWorkflowSessionModalDD] = useState({open: false, isFirstTime: false});

  // wf session modal popup for Password
  const [workflowSessionModalPW, setWorkflowSessionModalPW] = useState({open: false, isFirstTime: false});

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
        <TextField
          label={t('LBL_BASE_URL').replace(':', '')}
          variant="outlined"
          value={baseUrlValue}
          size='small'
          style={{ flexGrow: 1 }}
        />
        <LightTooltip title={t('COPY_URL')} arrow placement="top">
          <IconButton size="small" onClick={() => copyToClipboard(baseUrlValue)}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </LightTooltip>
      </div>

      <div style={{ margin: "8px 0 4px 0", display: "flex", gap: "5px" }}>
        <LabeledCheckbox
          sx={{
            marginLeft: "-8px",
            marginRight: 0,
            "& .MuiCheckbox-root": {
              paddingLeft: 0
            }
          }}
          checked={showCustomUrl}
          handleCheck={handleShowCustomUrlChange}
          label="Use Custom URL"
        />
      </div>
      {showCustomUrl ? (
        <div style={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "nowrap", overflowX: "auto", margin: "2px 0px 10px 0px" }}>
          <div style={{ flexGrow: 1, minWidth: 0, width: "calc(100% - 34px)" }}>
            <CustomUrlComposer
              label={t('LBL_CUSTOM_URL').replace(':', '')}
              prefix={customUrlBase}
              suffix={queryString}
              value={customUrlValue}
              placeholder="my/custom/path"
              onChange={handleCustomUrlChange}
            />
          </div>
          <LightTooltip title={t('COPY_CUSTOM_URL')} arrow placement="top">
            <IconButton
              size="small"
              sx={{ flexShrink: 0 }}
              onClick={() => copyToClipboard(customUrlFullValue || `${customUrlBase}${customUrlValue}`)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </LightTooltip>
        </div>
      ) : null}
      <div style={{display: "flex", alignItems: "center", columnGap: "10px", flexWrap: "wrap"}}>
        <FormControl variant="outlined" size="small" style={{flexGrow: '1', minWidth: "120px"}}>
          <InputLabel>{t("LBL_AUTHENTICATION").replace(":", "")}</InputLabel>
          <Select 
              size='small' 
              // className='width-100'
              MenuProps={{
                PaperProps: {
                  sx: {
                  maxHeight: 300 
                  }
                }
              }}
              value={selectedNode?.INBOUND?.AUTHENTICATE ? (selectedNode?.INBOUND?.AUTHENTICATE === "FALSE" ? "NO_AUTH" : selectedNode?.INBOUND?.AUTHENTICATE ) : 'TRUE'} 
              onChange={handleAuthDropdownChange}
          >
              {authDropdownOptions}
          </Select>
        </FormControl>
        <LabeledCheckbox
          style={{flexGrow: '2'}}
          checked={selectedNode?.INBOUND?.BTN_SEND_SYNC_RESPONSE === "TRUE" ? true : false}
          label={t("BTN_SEND_SYNC_RESPONSE")}
          handleCheck={(checked) => {
            handleSyncResChange(checked);
          }}
        />

        <LabeledCheckbox
          style={{flexGrow: '2'}}
          checked={selectedNode?.INBOUND?.REC_POST_DATA === "TRUE" ? true : false}
          label={t("BTN_WS_RECEIVE_POST_DATA")}
          handleCheck={(checked) => {
            handleRecPostDataChange(checked);
          }}
        />
      </div>

      <div style={{marginTop: "5px", display: "flex", flexDirection: "column", gap: "5px"}}>

        {/* "Use Decompression" - ZIP - checkbox */}
        {selectedNode?.INBOUND?.REC_POST_DATA === "TRUE" && (
          <LabeledCheckbox
            checked={selectedNode?.INBOUND?.ZIP === "TRUE" ? true : false}
            label={t("BTN_USE_ZIP_DECOMPRESS")}
            handleCheck={(checked) => {
              handleDecompressionChange(checked);
            }}   
          />
        )}

        {/* "Specify Decompression Directory (optional)" - state only - checkbox */}
        {selectedNode?.INBOUND?.ZIP === "TRUE" && (
          <LabeledCheckbox
            checked={specDecDir}
            label={t("LBL_SPECIFY_DECOMPRESSION_DIRECTORY")}
            handleCheck={(checked) => {
              handleSpecDecDirChange(checked);
            }}
          />
        )}

        {/* Decompression Directory text field - render if "Use Decompression" and "Specify Decompression Directory (optional)" are checked */}
        {(selectedNode?.INBOUND?.ZIP === "TRUE" && specDecDir) && (
          <div style={{display: "flex", marginTop: "10px", gap: "5px"}}>
            <TextField
              label={t('DECOMPRESSION_DIRECTORY')}
              variant="outlined"
              value={decDirTextVal}
              size='small'
              onChange={(e) => handleDecDirTextChange(e?.target?.value)}
              style={{flexGrow: "1"}}
            />
            <Button 
              size='small' 
              variant='outlined' 
              onClick={() => setWorkflowSessionModalDD({open: true, isFirstTime: true})} 
              sx={{width: '40px', minWidth:'40px'}}
            >
              <MoreHorizOutlined fontSize='small' />
            </Button>
          </div>
        )}

        {/* password container - render if Use Decompression is checked*/}
        {selectedNode?.INBOUND?.ZIP === "TRUE" && (
          <div style={{display: "flex", marginTop: "10px", alignItems: "center"}}>

            {/* Password checkbox */}
            <div>
              <LabeledCheckbox
                checked={selectedNode?.INBOUND?.USE_ZIP_PASSWORD === "TRUE"}
                label={t("BTN_USE_ZIP_PASSWORD")}
                handleCheck={handleUseZipPasswordChange}  
              />
            </div>
            
            {/* Password text field and wfs popup button */}
            {selectedNode?.INBOUND?.USE_ZIP_PASSWORD === "TRUE" && (
              <div style={{display: "flex", flexGrow: "1", gap: "5px"}}>
                <SmartPasswordField
                  variant="outlined"
                  size="small"
                  label={t("LBL_WS_PASSWORD")}
                  type="password"
                  value={passwordText || ''}
                  onChange={(e) => handlePasswordTextChange(e?.target?.value)}
                  className={"width-100"}
                  name={"ZIP_PASSWORD"}
                  autoComplete="off"
                />
                {/* wfs popup */}
                <Button 
                  size='small' 
                  variant='outlined' 
                  onClick={() => setWorkflowSessionModalPW({open: true, isFirstTime: true})} 
                  sx={{width: '40px', minWidth:'40px'}}
                >
                  <MoreHorizOutlined fontSize='small' />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      
      <div style={{marginTop: "10px"}}>
        <ResizableBox>
          <CommonGrid
            rowData={treeData}
            gridOptions={gridOptions}
            containerStyle={{ height: "100% !important" }}
            gridResizable={true}
            getGridAPI={(e, gridRef) => {
              setGridApi(gridRef.api);
              gridApiRef.current = gridRef.api;
              gridApiRef?.current?.forEachNode((node, index) => {
                if (index === 0) {
                  node.setSelected(true);
                  gridApiRef?.current.ensureNodeVisible(node);
                }
              });
            }}
            gridProps={{
              overlayNoRowsTemplate: t('NO_DATA_MSSG'),
              rowSelection: 'single',
              onSelectionChanged: (event) => {
                const selected = event.api.getSelectedRows()?.[0];
                if (selected) {
                  setTreeSelectedKeys([selected.key]);
                  setTreeSelectedNode(selected);
                }
              }
            }}
          />
        </ResizableBox>

        {/* node info */}
        
        {(treeData.length > 0 && treeSelectedKeys.length > 0) && (
          <div style={{marginTop: "16px"}}>
            <TextField
              label={t('LBL_WS_VALUE_NAME')}
              variant="outlined"
              value={valueNameText}
              onChange={onValueNameTextChange}
              size='small'
              style={{width: "100%"}}
            />

            <div 
              style={{
                marginTop: "8px", 
                marginBottom: "16px", 
                // display: treeData?.length <= 1 ? "none": "unset"
              }}
            > 
              <FormControl>
                <RadioGroup
                  aria-labelledby="wf-state-label"
                  value={treeSelectedNode?.WRITE_TO_RESPONSE === "TRUE" ? "sourceData" : treeSelectedNode?.MAPNAME ? "wfSession" : ""}
                  onChange={handleSourceDataCbValue}
                  name="wf-trans-details"

                  row
                >
                  {/* <FormControlLabel value="sourceData" control={<Radio />} label={t("Use As Source Data")} disabled={treeData.length <= 1 || selectedNode?.INBOUND?.REC_POST_DATA === "TRUE"}/> */}
                  <FormControlLabel value="sourceData" control={<Radio />} label={t("Use As Source Data")} disabled={selectedNode?.INBOUND?.REC_POST_DATA === "TRUE"}/>

                  {/* <FormControlLabel value="wfSession" control={<Radio />} label={t("Add to Workflow Session")} disabled={treeData.length <= 1}/> */}
                  <FormControlLabel value="wfSession" control={<Radio />} label={t("Add to Workflow Session")} />

                </RadioGroup>
              </FormControl>
            </div>
          </div>
        )}
          
        {/* <WorkflowEncryptDecrypt screen={"GET_DATA"} updateAndSaveData={saveDecryptionComponent}/> */}
      </div>

      {/* decompression directory text - wf session modal popup */}
      <WorkflowSessionModal
        valueLabel={t("DECOMPRESSION_DIRECTORY")}
        showAccountData={true}
        showCallingWorkflow={true}
        showDateAndTime={true} 
        showSystemData={true}
        showWorkflowSession={true}
        openModal={workflowSessionModalDD} 
        setModalOpen={setWorkflowSessionModalDD}
        handleClose={() => setWorkflowSessionModalDD({open: false, isFirstTime: false})}
        initialValue={decDirTextVal}
        valuesHandler={(value) => {
          handleDecDirTextChange(value);
        }}
      />

      {/* password text - wf session modal popup */}
      <WorkflowSessionModal
        // valueLabel={passwordText === '' ? t("LBL_WS_PASSWORD") : passwordText} // use this if we want to show the password as a label. SWT does this but it looks like it is a bug.
        valueLabel={t("LBL_WS_PASSWORD")}
        showAccountData={true}
        showCallingWorkflow={true}
        showDateAndTime={true} 
        showSystemData={true}
        showWorkflowSession={true}
        openModal={workflowSessionModalPW} 
        setModalOpen={setWorkflowSessionModalPW}
        handleClose={() => setWorkflowSessionModalPW({open: false, isFirstTime: false})}
        initialValue={passwordText}
        inputType="password"
        specialPasswordField={true}
        valuesHandler={(value) => {
          handlePasswordTextChange(value);
        }}
      />
    </>

  )
}